import {
  createAsyncThunk,
  createSlice,
  current,
  createListenerMiddleware,
  createAction,
  Action,
} from '@reduxjs/toolkit';

import {
  AddressData,
  AdminAddressApi,
  EntityCustomShippingFeesApi,
  EntityDistanceShippingFeesApi,
  EntityLocationShippingFeesApi,
  OrderApi,
  ProductApi,
  QuickUpdateOrderBody,
  UpdateOrderBody,
} from 'api';
import { RootState } from 'app';
import { PROMOTION_TYPE } from 'containers/SaleManager/Discount/Discount/commons';
import { ProductOption } from 'containers/WarehouseOperation/WorkSpace/components/SearchResult/SearchResultItem/interfaces';
import {
  ADJUSTMENT_TYPE,
  compareAddress,
  convertAdditionalInfoAddressToAddressData,
  isWholesaleOrder,
  ORDER_DELIVERY_METHOD,
  ORDER_PAYMENT_METHOD,
  SummaryOrderType,
  ValidateConstraintsType,
} from 'utils';

export const updateOrderSliceListenerMiddleware = createListenerMiddleware();

export const takeLastedCalcWholesaleProductItemWhenUpdateOrder = createAction<
  any | undefined
>('takeLastedCalcWholesaleProductItemWhenUpdateOrder');

updateOrderSliceListenerMiddleware.startListening({
  actionCreator: takeLastedCalcWholesaleProductItemWhenUpdateOrder,
  effect: async (action: Action, listenerApi: any) => {
    // cancel other running instances
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(800); // delay to wait next action before dispatching final one

    const { payload } = action as any;
    listenerApi.dispatch(
      checkOrderItemQuantityForUpdateOrder({
        body: payload,
        isUpdatingProductItem: true,
      })
    );
  },
});

export const quickUpdateOrderBySupplier = createAsyncThunk(
  'updateOrder/quickUpdateOrderBySupplier',
  async (
    params: { id: string; options: any; body: QuickUpdateOrderBody },
    { rejectWithValue }
  ) => {
    try {
      const { id, options, body } = params;
      const response = await OrderApi.quickUpdateOrderV2(id, options, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrderBySupplier = createAsyncThunk(
  'updateOrder/updateOrderBySupplier',
  async (
    params: { id: string; body: UpdateOrderBody },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.updateOrder(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDraftOrderItems = createAsyncThunk(
  'updateOrder/findDraftOrderItems',
  async (params: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await OrderApi.findDraftOrderItems(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOrderToUpdateForCustomerBySupplier = createAsyncThunk(
  'updateOrder/findOrderToUpdateForCustomerBySupplier',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await OrderApi.findOrderToUpdateForCustomerBySupplier(
        id
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createAddressForPartnerUpdateOrder = createAsyncThunk(
  'updateOrder/createAddressForPartnerUpdateOrder',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    try {
      const { id, body } = param;
      const response = await AdminAddressApi.create(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const removeAddressForCustomer = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>('updateOrder/removeAddressForCustomer', async (id, { rejectWithValue }) => {
  try {
    const response = await AdminAddressApi.delete(id);
    return response;
  } catch (error: any) {
    if (!error.response) {
      throw error;
    }

    return rejectWithValue(error.response.data);
  }
});

export const removeAddressForPartnerUpdateOrder = createAsyncThunk<
  any,
  string,
  { rejectValue: { message: string } }
>(
  'updateOrder/removeAddressForPartnerUpdateOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.delete(id);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAddressForPartnerUpdateOrder = createAsyncThunk<
  any,
  { id: string; body: AddressData },
  { rejectValue: { message: string } }
>(
  'updateOrder/updateAddressForPartnerUpdateOrder',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await AdminAddressApi.update(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findProductToUpdateOrder = createAsyncThunk<
  any,
  { id: string; body: any },
  { rejectValue: { message: string } }
>(
  'updateOrder/findProductToUpdateOrder',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await ProductApi.findProductToUpdateOrder(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkOrderItemQuantityForUpdateOrder = createAsyncThunk(
  'updateOrder/checkOrderItemQuantityForUpdateOrder',
  async (
    params: { body: UpdateOrderBody; isUpdatingProductItem?: boolean },
    { rejectWithValue }
  ) => {
    const { body } = params;

    try {
      const response =
        await OrderApi.checkOrderItemQuantityToCreateOrUpdateOrder(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findListAddressForCustomer = createAsyncThunk(
  'updateOrder/findListAddressForCustomer',
  async (param: { id: string; query: any }, { rejectWithValue }) => {
    try {
      const { id, query } = param;

      const response = await AdminAddressApi.filter(id, query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityCustomShippingFeeToUpdateOrder = createAsyncThunk(
  'entityCustomShippingFee/findEntityCustomShippingFeeToUpdateOrder',
  async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityCustomShippingFeesApi.findToUpdateOrder(
        orderID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityDistanceShippingFeeToUpdateOrder = createAsyncThunk(
  'entityDistanceShippingFee/findEntityDistanceShippingFeeToUpdateOrder',
  async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityDistanceShippingFeesApi.findToUpdateOrder(
        orderID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityShippingPolicyItemToUpdateOrder = createAsyncThunk(
  'entityShippingPolicyItem/findEntityShippingPolicyItemToUpdateOrder',
  async ({ orderID }: { orderID: string }, { rejectWithValue }) => {
    try {
      const response = await EntityLocationShippingFeesApi.findToUpdateOrder(
        orderID
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export interface ItemType {
  id: string;
  code: string;
  orderItemID?: string;
  proposalItemID?: string;
  productID?: string;
  name: string;
  avatar: string;

  reportQuantity: any;
  price: number;
  quantity: number;
  priceUnit: any;
  oldPriceUnit?: any;
  priceUnitList: any[];
  currentPrice: number;
  originPrice: number;
  supplierNote: string;
  customerNote: string;
  customerQuantity: number;
  soldQuantity: number;
  originCustomerQuantity?: number;
  productOptions: ProductOption[];
  productPricePolicyID?: string;
  productProposalPolicyID?: string;
  salePromotionConditions?: any[];
}

interface IState {
  selectedProducts: any[];
  isWholesaleOrder?: boolean;
  customerID: string;
  chainCustomerID: string;
  customerOwnerID: string | undefined;

  deliveryMethod: ORDER_DELIVERY_METHOD;
  paymentMethod: ORDER_PAYMENT_METHOD;
  address: AddressData | undefined;

  bankAccount: any | undefined;
  deliveryNote: any | undefined;
  images: any[];
  deliveryFee: number;
  deliveryFeeType: number;

  summaryOrder: SummaryOrderType;
  promotions?: any[];
  selectedOrderSalePromotionCondition?: any;

  validationResult: any;

  totalCustomerQuantity: number;

  isProductNotEnoughQuantity?: boolean;

  customerNote: string;
  supplierNote: string;

  addressData: any[];
  orderID: string;

  orderCode: string;
  order: any;

  choiceItemList: any[];

  customer: any;
  supplier: any;
  partnerID: string | undefined;

  isLoading: boolean;
  shouldUpdateOrderData: boolean;

  oldEntityPricePolicyID?: string;
  entityPricePolicyID?: string;
  oldEntityProposalPolicyID?: string;
  entityProposalPolicyID?: string;

  isCheckingOrderItemQuantity: boolean;

  // phí giao hàng
  isLoadingCustomShippingFees: boolean;
  customShippingFees?: any[];
  isLoadingDistanceShippingFees: boolean;
  distanceShippingFees?: any[];
  isLoadingShippingPolicyItems: boolean;
  shippingPolicyItems?: any[];
  locationShippingFee: any | undefined;
  distanceShippingFee: any | undefined;
}

const validationResultInitialState: ValidateConstraintsType = {
  isValid: true,
  // deleted proposal items
  isProductItemDeleted: false,
  deletedProductItemIDs: [],
  deletedOrderItemIDs: [],
  // out of stock/ not enough proposal items
  isProductItemNotEnoughQuantity: false,
  notEnoughQuantityProductItems: [],
  notEnoughQuantityOrderItems: [],
  deactivatedProductIDs: [],
  isProductDeactivated: false,
  // price-changed proposal items
  isProductItemPriceChanged: false,
  priceChangedProductItems: [],
  priceChangedOrderItems: [],
  notEnoughQuantityProducts: [],
  isProductPriceChanged: false,
  priceChangedProducts: [],
  isAddressChanged: false,
  changedAddress: undefined,
};

const initialState: IState = {
  selectedProducts: [],
  customerID: '',
  chainCustomerID: '',
  customerOwnerID: '',
  orderID: '',
  orderCode: '',
  order: {},
  customer: {},
  supplier: {},

  deliveryMethod: ORDER_DELIVERY_METHOD.PLATFORM_DELIVERY,
  paymentMethod: ORDER_PAYMENT_METHOD.CASH,
  address: undefined,

  bankAccount: undefined,
  deliveryNote: undefined,
  images: [],
  deliveryFee: 0,
  deliveryFeeType: 0,

  summaryOrder: {
    deliveryFee: 0,
    deliveryFeeType: 0,
    totalPrice: BigInt(0),
    totalTemporaryPrice: BigInt(0),
    totalItem: 0,
    totalAdjustment: BigInt(0),
    adjustments: [],
  },
  promotions: [],

  validationResult: validationResultInitialState,
  totalCustomerQuantity: 0,
  customerNote: '',
  supplierNote: '',
  addressData: [],
  choiceItemList: [],
  isWholesaleOrder: false,
  isLoading: true,
  shouldUpdateOrderData: false,
  partnerID: undefined,

  oldEntityPricePolicyID: undefined,
  entityPricePolicyID: undefined,
  oldEntityProposalPolicyID: undefined,
  entityProposalPolicyID: undefined,

  isCheckingOrderItemQuantity: false,

  isLoadingCustomShippingFees: false,
  customShippingFees: [],
  isLoadingDistanceShippingFees: false,
  distanceShippingFees: [],
  isLoadingShippingPolicyItems: false,
  shippingPolicyItems: [],
  locationShippingFee: undefined,
  distanceShippingFee: undefined,
};

const initialSummaryOrder: SummaryOrderType = {
  deliveryFee: 0,
  deliveryFeeType: 0,
  totalPrice: BigInt(0),
  totalTemporaryPrice: BigInt(0),
  totalItem: 0,
  totalAdjustment: BigInt(0),
  adjustments: [],
};

const updateOrderSlice = createSlice({
  name: 'updateOrder',
  initialState,
  reducers: {
    updateInformationUpdateOrder(state, { payload }) {
      const orderInfo = payload;

      Object.keys(orderInfo).forEach(key => {
        if (orderInfo[key] != undefined) {
          state[key] = orderInfo[key];
        }

        if (key == 'entityPricePolicyID') {
          state.oldEntityPricePolicyID = state.entityPricePolicyID;
        }

        if (key == 'entityProposalPolicyID') {
          state.oldEntityProposalPolicyID = state.entityProposalPolicyID;
        }
      });
    },
    updateBankAccountInfoUpdateOrderData(state, { payload }) {
      if (
        state.bankAccount &&
        payload.bankAccount &&
        payload.bankAccount.id == state.bankAccount.id
      ) {
        state.bankAccount = undefined;
      } else {
        state.bankAccount = payload.bankAccount;
      }
    },

    updateDeliveryNoteUpdateOrderData(state, { payload }) {
      state.deliveryNote = payload.deliveryNote;
    },
    updatePartnerAddressData(state, { payload }) {
      state.addressData = payload;
    },
    updateCustomerNote(state, { payload }) {
      state.customerNote = payload;
    },
    updateSupplierNoteForUpdateOrder(state, { payload }) {
      state.supplierNote = payload;
    },
    updateSelectedProducts(state, { payload }) {
      const newSelectedProductItems = payload;
      state.selectedProducts = newSelectedProductItems;

      if (newSelectedProductItems.length === 0) {
        state.summaryOrder = initialSummaryOrder;
      }
    },
    updateSelectedProductUpdateOrder(
      state,
      {
        payload,
      }: {
        payload: {
          id: string;
          quantity?: number;
          originPrice?: number;
        };
      }
    ) {
      const { id, quantity, originPrice } = payload;
      const selectedProduct = state.selectedProducts.find(
        item => item.id == id
      );
      if (selectedProduct && quantity != undefined) {
        selectedProduct.customerQuantity = Number(quantity);
      }
      if (selectedProduct && originPrice != undefined) {
        selectedProduct.originPrice = Number(originPrice);
      }
      if (selectedProduct?.promotions && selectedProduct?.promotions.length) {
        selectedProduct.promotions.forEach(promotion => {
          if (promotion.type === PROMOTION_TYPE.RATE && promotion.rate > 0) {
            promotion.adjustment = Math.round(
              (selectedProduct.originPrice *
                selectedProduct.customerQuantity *
                promotion.rate) /
                100
            );
          } else if (
            promotion.type === PROMOTION_TYPE.AMOUNT &&
            promotion.amount > 0
          ) {
            const amount =
              promotion.amount >= selectedProduct.originPrice
                ? selectedProduct.originPrice
                : promotion.amount;

            promotion.adjustment = Math.round(
              amount * selectedProduct.customerQuantity
            );
          }
        });
      }
    },
    setChoiceItemList(state, { payload }) {
      state.choiceItemList = payload;
    },
    handleResetUpdateOrderData(state) {
      state.selectedProducts = initialState.selectedProducts;
      state.validationResult = initialState.validationResult;
      state.customerID = initialState.customerID;
      state.chainCustomerID = initialState.chainCustomerID;
      state.orderID = initialState.orderID;
      state.orderCode = initialState.orderCode;
      state.deliveryMethod = initialState.deliveryMethod;
      state.paymentMethod = initialState.paymentMethod;
      state.address = initialState.address;
      state.addressData = initialState.addressData;
      state.customer = initialState.customer;
      state.supplier = initialState.supplier;
      state.totalCustomerQuantity = initialState.totalCustomerQuantity;
      state.entityPricePolicyID = initialState.entityPricePolicyID;
      state.oldEntityPricePolicyID = initialState.oldEntityPricePolicyID;
      state.promotions = [];

      state.promotions = initialState.promotions;
      state.selectedOrderSalePromotionCondition = {};
      state.shippingPolicyItems = initialState.shippingPolicyItems;
      state.distanceShippingFees = initialState.distanceShippingFees;
      state.customShippingFees = initialState.customShippingFees;
      state.locationShippingFee = initialState.locationShippingFee;
      state.distanceShippingFee = initialState.distanceShippingFee;
      state.deliveryNote = initialState.deliveryNote;
      state.deliveryFee = initialState.deliveryFee;
      state.deliveryFeeType = initialState.deliveryFeeType;
      state.isCheckingOrderItemQuantity =
        initialState.isCheckingOrderItemQuantity;
      state.isLoadingShippingPolicyItems =
        initialState.isLoadingShippingPolicyItems;
      state.isLoadingDistanceShippingFees =
        initialState.isLoadingDistanceShippingFees;
      state.isLoadingCustomShippingFees =
        initialState.isLoadingCustomShippingFees;
    },
    setReloadUpdateOrder(state, { payload }) {
      state.isLoading = payload;
    },
    updateShouldUpdateOrderData(state, { payload }) {
      state.shouldUpdateOrderData = payload;
    },
    revertStateWhenHasBeError(state) {
      state.selectedProducts = [...state.selectedProducts];
    },
    resetValidationResultUpdateOrder: state => {
      state.validationResult = validationResultInitialState;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      findOrderToUpdateForCustomerBySupplier.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const {
          order,
          summaryOrder,
          selectedOrderItems,
          totalCustomerQuantity,
        } = data;

        const {
          customer,
          supplier,
          additionalInfo,
          paymentMethod,
          customerNote,
          supplierNote,
          type,
          orderItems,
          customerID,
          chainCustomerID,
          deliveryMethod,
        } = order;

        const orderAddress: any = convertAdditionalInfoAddressToAddressData(
          additionalInfo.address
        );
        if (
          state.address &&
          state.address?.key?.includes('addition-address-order')
        ) {
          state.addressData = state.addressData.filter(
            item => !item.notExisted
          );
        }
        state.address = {
          ...orderAddress,
          disabledUpdateRemove: true,
          key: `${orderAddress.id}addition-address-order`,
        };
        if (state.addressData.length > 0) {
          const foundAddressIndex = state.addressData.findIndex(
            item => item.id === orderAddress.id
          );
          const oldOrderAddress = {
            ...orderAddress,
            disabledUpdateRemove: true,
            key: `${orderAddress.id}addition-address-order`,
          };
          if (
            compareAddress(
              oldOrderAddress,
              state.addressData[foundAddressIndex]
            ) == false
          ) {
            state.addressData = [
              { ...oldOrderAddress, notExisted: true },
              ...state.addressData,
            ];
          } else {
            const newAddressList = [...state.addressData];
            newAddressList[foundAddressIndex].disabledUpdateRemove = true;
            state.addressData = newAddressList;
          }
        }

        state.entityPricePolicyID = data?.entityPricePolicyID;
        state.oldEntityPricePolicyID = data?.entityPricePolicyID;
        state.entityProposalPolicyID = data?.entityProposalPolicyID;
        state.oldEntityProposalPolicyID = data?.entityProposalPolicyID;

        state.order = order;
        state.paymentMethod = paymentMethod;
        state.deliveryMethod = deliveryMethod;
        state.customerNote = customerNote;
        state.supplierNote = supplierNote;
        state.summaryOrder = summaryOrder;
        state.totalCustomerQuantity = totalCustomerQuantity;
        state.customerID = customer?.id || customerID;
        state.chainCustomerID = chainCustomerID;
        state.orderID = order.id;
        state.orderCode = order.code;
        state.customerOwnerID = order?.customer?.ownerID ?? order?.customer?.id;
        state.partnerID = order?.partnerID;
        state.deliveryFee = order.deliveryFee;
        state.deliveryFeeType = order?.deliveryFeeType ?? 0;
        state.promotions = summaryOrder.adjustments
          .filter((item: any) => item.type == ADJUSTMENT_TYPE.PROMOTION)
          .map((item: any) => item.info);

        const findOrderSalePromotionCondition =
          summaryOrder.orderSalePromotionConditions.find(
            (item: any) => item.isSelected
          );

        state.selectedOrderSalePromotionCondition =
          findOrderSalePromotionCondition;

        state.images = order?.orderMedias
          .filter((item: any) => item.type == 1)
          .map((mapItem: any) => {
            return { ...mapItem.media, uid: mapItem.media.id };
          });

        state.customer = customer;
        state.supplier = supplier;
        state.isWholesaleOrder = isWholesaleOrder(type);
        state.bankAccount = order?.additionalInfo?.bankAccount ?? undefined;
        state.deliveryNote = order?.additionalInfo?.transportNote ?? undefined;

        state.selectedProducts = orderItems?.map(item => {
          const findSelectedOrderItem = selectedOrderItems?.find(
            selectedOrderItem => selectedOrderItem?.orderItemID == item.id
          );

          return {
            id: item.id,
            code: item?.supplierProduct?.code ?? item?.product?.code ?? '',
            orderItemID: item.id,
            productID: item.productID,
            proposalItemID: findSelectedOrderItem?.proposalItemID,
            name: item?.product?.name,
            avatar: item.product?.avatar,
            originCustomerQuantity: findSelectedOrderItem?.customerQuantity,
            reportQuantity: findSelectedOrderItem?.reportQuantity,
            price: item.price,
            quantity: findSelectedOrderItem?.customerQuantity,
            priceUnit: findSelectedOrderItem?.priceUnit,
            priceUnitList: findSelectedOrderItem?.priceUnitList
              ?.filter((price: any) => price.isSelected)
              .map((priceUnit: any) => {
                if (findSelectedOrderItem?.priceUnit.id == priceUnit.id) {
                  return { ...priceUnit, isSupplierSelected: true };
                }
                return { ...priceUnit, isSupplierSelected: false };
              }),
            promotions: findSelectedOrderItem?.promotions,
            supplierNote: item?.supplierNote || '',
            customerNote: item?.customerNote || '',
            currentPrice:
              findSelectedOrderItem?.currentPrice ??
              findSelectedOrderItem?.priceUnit.price,
            originPrice:
              findSelectedOrderItem?.originPrice ??
              findSelectedOrderItem?.priceUnit.price,
            customerQuantity: findSelectedOrderItem?.customerQuantity,
            soldQuantity: item?.product?.soldQuantity,
            supplierProductOptions: item?.supplierProductOptions,
            productPricePolicyID: findSelectedOrderItem?.productPricePolicyID,
            productProposalPolicyID:
              findSelectedOrderItem?.productProposalPolicyID,
            salePromotionConditions:
              findSelectedOrderItem?.salePromotionConditions ?? [],
            onHand: findSelectedOrderItem?.onHand,
            available: findSelectedOrderItem?.available,
            entityProductID: findSelectedOrderItem?.entityProductID,
          };
        });

        state.isLoading = false;
      }
    );
    builder.addCase(
      findOrderToUpdateForCustomerBySupplier.rejected,
      (state, action) => {
        state.isLoading = false;
      }
    );
    builder.addCase(
      checkOrderItemQuantityForUpdateOrder.pending,
      (state, action) => {
        state.isCheckingOrderItemQuantity = true;
      }
    );
    builder.addCase(
      checkOrderItemQuantityForUpdateOrder.rejected,
      (state, action) => {
        state.isCheckingOrderItemQuantity = false;
      }
    );
    builder.addCase(
      checkOrderItemQuantityForUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const {
          summaryOrder,
          validationResult,
          selectedProducts,
          totalCustomerQuantity,
        } = data;
        const { isUpdatingProductItem, body } = action.meta.arg;

        state.shouldUpdateOrderData = !isUpdatingProductItem;
        state.entityPricePolicyID = body.entityPricePolicyID;
        state.oldEntityPricePolicyID = data?.oldEntityPricePolicyID;

        if (data.validationResult && !data.validationResult.isValid) {
          state.shouldUpdateOrderData = true;
          state.validationResult = data.validationResult;
          buildItems(state, selectedProducts);
          updateItemsWithValidationResult(state);
        }

        if (!isUpdatingProductItem) {
          buildItems(state, selectedProducts);
        }

        const tempSelectedProducts = selectedProducts?.map(item => {
          let findSelectedProduct = current(state)?.selectedProducts?.find(
            (selectedItem: any) => selectedItem.productID == item.productID
          );

          if (findSelectedProduct) {
            findSelectedProduct = {
              ...findSelectedProduct,
              promotions: item?.promotions,
              salePromotionConditions: item?.salePromotionConditions ?? [],
            };

            return findSelectedProduct;
          }

          if (item.productID) {
            let selectedItem = current(state).choiceItemList.find(
              productItem => productItem.productID == item.productID
            );

            if (selectedItem) {
              selectedItem = {
                ...selectedItem,
                promotions: item?.promotions,
                salePromotionConditions: item?.salePromotionConditions ?? [],
              };

              return selectedItem;
            }
          }

          return item;
        });

        state.selectedProducts = tempSelectedProducts;

        state.deliveryFee = summaryOrder.deliveryFee;
        state.deliveryFeeType = summaryOrder.deliveryFeeDetail.deliveryFeeType;
        state.validationResult = validationResult;
        state.summaryOrder = summaryOrder;
        state.totalCustomerQuantity = totalCustomerQuantity;
        state.promotions = summaryOrder.adjustments
          .filter(
            (item: any) =>
              item.type == ADJUSTMENT_TYPE.PROMOTION && item.price > 0
          )
          .map((item: any) => item.info);

        const findOrderSalePromotionCondition =
          summaryOrder.orderSalePromotionConditions.find(
            (item: any) => item.isSelected
          );

        state.selectedOrderSalePromotionCondition =
          findOrderSalePromotionCondition;

        state.locationShippingFee =
          summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem;
        state.distanceShippingFee =
          summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee;
        state.customShippingFees = (state.customShippingFees || []).map(
          item => {
            const foundItem = (
              summaryOrder?.deliveryFeeDetail?.customShippingFees || []
            ).find(item1 => item1.id == item.id);
            if (foundItem) {
              return {
                ...item,
                ...foundItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );
        state.shippingPolicyItems = (state.shippingPolicyItems || []).map(
          item => {
            if (
              summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryOrder?.deliveryFeeDetail?.entityShippingPolicyItem,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );

        state.distanceShippingFees = (state.distanceShippingFees || []).map(
          item => {
            if (
              summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee?.id ==
              item.id
            ) {
              return {
                ...item,
                ...summaryOrder?.deliveryFeeDetail?.entityDistanceShippingFee,
                isSelected: true,
              };
            }
            return { ...item, isSelected: false };
          }
        );

        state.isCheckingOrderItemQuantity = false;
      }
    );

    builder.addCase(
      createAddressForPartnerUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const newAddress = {
          ...data,
          key: data.id,
        };
        state.addressData = [...state.addressData, newAddress];
      }
    );
    builder.addCase(
      updateAddressForPartnerUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;

        const foundAddressIndex = state.addressData.findIndex(
          item => item.id === data?.id
        );
        if (foundAddressIndex != -1) {
          state.addressData[foundAddressIndex] = {
            ...data,
            isSupplierCreateForPartner: true,
          };
        }
        if (state.address?.id === data?.id) {
          state.address = data;
        } else if (!state.customer?.id) {
          state.address = { ...state.address, ...data };
        }
      }
    );
    builder.addCase(findListAddressForCustomer.fulfilled, (state, action) => {
      const { data } = action.payload;
      if (data?.length > 0) {
        const keyAddressData = data.map(item => {
          return { ...item, key: item.id };
        });
        const foundAddressIndex = keyAddressData.findIndex(
          item => item.id == state.address?.id
        );
        const oldOrderAddress = {
          ...state.address,
          disabledUpdateRemove: true,
        };
        if (foundAddressIndex === -1) {
          state.addressData = [oldOrderAddress, ...keyAddressData];
        } else {
          if (state.address) {
            if (
              compareAddress(
                state.address,
                keyAddressData[foundAddressIndex]
              ) == false
            ) {
              state.addressData = [
                { ...oldOrderAddress, notExisted: true },
                ...keyAddressData,
              ];
            } else {
              const newAddressList = [...keyAddressData];
              newAddressList[foundAddressIndex].disabledUpdateRemove = true;
              state.addressData = newAddressList;
            }
          }
        }
      } else if (state.address) {
        state.addressData = [state.address];
      }
    });
    builder.addCase(
      removeAddressForPartnerUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        const foundAddressIndex = state.addressData.findIndex(
          item => item.id === data?.id
        );
        if (foundAddressIndex !== -1) {
          state.addressData.splice(foundAddressIndex, 1);
        }
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToUpdateOrder.pending,
      (state, response) => {
        state.isLoadingCustomShippingFees = true;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToUpdateOrder.rejected,
      (state, response) => {
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityCustomShippingFeeToUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.customShippingFees = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          return item;
        });
        state.isLoadingCustomShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateOrder.pending,
      (state, response) => {
        state.isLoadingDistanceShippingFees = true;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateOrder.rejected,
      (state, response) => {
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityDistanceShippingFeeToUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.distanceShippingFees = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          if (item.isSelected) {
            state.distanceShippingFee = item;
          }
          return item;
        });
        state.isLoadingDistanceShippingFees = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateOrder.pending,
      (state, response) => {
        state.isLoadingShippingPolicyItems = true;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateOrder.rejected,
      (state, response) => {
        state.isLoadingShippingPolicyItems = false;
      }
    );
    builder.addCase(
      findEntityShippingPolicyItemToUpdateOrder.fulfilled,
      (state, action) => {
        const { data } = action.payload;
        state.shippingPolicyItems = data?.map(item => {
          item.isSelected = item.orderID != undefined;
          if (item.isSelected) {
            state.locationShippingFee = item;
          }
          return item;
        });
        state.isLoadingShippingPolicyItems = false;
      }
    );
  },
});

const updateItemsWithValidationResult = (state: IState) => {
  const {
    deletedOrderItemIDs,
    deletedProductItemIDs,
    isProductItemDeleted,
    isProductItemNotEnoughQuantity,
    notEnoughQuantityProductItems,
    notEnoughQuantityOrderItems,
    isProductNotEnoughQuantity,
    notEnoughQuantityProducts,
    isPriceChanged,
    isProductDeactivated,
    isReportQuantityChanged,
    reportQuantityChangedProducts,
    reportQuantityChangedOrderItems,
    deactivatedProductIDs,
    isAddressChanged,
    changedAddress,
    priceChangedProducts,
    priceChangedOrderItems,
    isOrderEntityShippingPolicyItemChanged,
    changedOrderEntityShippingPolicyItem,
    isOrderEntityDistanceShippingFeeChanged,
    changedOrderEntityDistanceShippingFee,
    isOrderEntityCustomShippingFeesChanged,
    changedOrderEntityCustomShippingFees,
  } = state.validationResult;

  let selectedProducts = [...state.selectedProducts];

  if (isProductItemDeleted) {
    selectedProducts = selectedProducts?.filter(
      item =>
        !(item.orderItemID && deletedOrderItemIDs?.includes(item.orderItemID))
    );
  }

  if (isReportQuantityChanged) {
    selectedProducts = selectedProducts?.map(item => {
      if (
        reportQuantityChangedOrderItems
          .map(
            (reportQuantityChangedOrderItem: any) =>
              reportQuantityChangedOrderItem.id
          )
          .includes(item.id)
      ) {
        const findOrderItem = reportQuantityChangedOrderItems.find(
          (reportQuantityChangedOrderItem: any) =>
            reportQuantityChangedOrderItem.id == item.id
        );

        return {
          ...item,
          ...(findOrderItem?.newReportQuantity &&
          Object.keys(findOrderItem?.newReportQuantity).length > 0
            ? {
                absoluteQuantity:
                  findOrderItem?.newReportQuantity?.absoluteQuantity,
                relativeQuantity:
                  findOrderItem?.newReportQuantity?.relativeQuantity,
                reportType: findOrderItem?.newReportQuantity?.reportType,
              }
            : {}),
          ...(findOrderItem?.productProposalPolicyID
            ? {
                productProposalPolicyID: findOrderItem?.productProposalPolicyID,
              }
            : {}),
        };
      }

      return item;
    });
  }

  if (isProductItemNotEnoughQuantity) {
    selectedProducts.forEach(item => {
      const notEnoughQuantityProductItem = notEnoughQuantityProductItems?.find(
        notEnoughQuantityItem =>
          notEnoughQuantityItem.id === item?.proposalItemID
      );

      if (notEnoughQuantityProductItem) {
        item.customerQuantity =
          notEnoughQuantityProductItem.newCustomerQuantity;
      }

      if (item.orderItemID) {
        const notEnoughQuantityOrderItem = notEnoughQuantityOrderItems?.find(
          notEnoughQuantityItem => notEnoughQuantityItem.id === item.orderItemID
        );

        if (notEnoughQuantityOrderItem) {
          item.customerQuantity =
            notEnoughQuantityOrderItem.newCustomerQuantity;
        }
      }
    });
  }

  if (isProductNotEnoughQuantity) {
    selectedProducts.forEach(item => {
      const notEnoughQuantityProductItem = notEnoughQuantityProducts?.find(
        notEnoughQuantityProduct =>
          notEnoughQuantityProduct.id === item?.productID
      );

      if (notEnoughQuantityProductItem) {
        item.customerQuantity =
          notEnoughQuantityProductItem.newCustomerQuantity;
      }
    });
  }

  if (isProductDeactivated) {
    selectedProducts.forEach(item => {
      if (item?.productID) {
        const notEnoughQuantityProductItem: string =
          deactivatedProductIDs?.find(
            deactivatedProductID => deactivatedProductID == item?.productID
          );

        if (notEnoughQuantityProductItem) {
          selectedProducts = selectedProducts.filter(selectedItem => {
            if (selectedItem?.productID) {
              if (selectedItem?.productID != notEnoughQuantityProductItem) {
                return true;
              }
            }

            return false;
          });
        }
      }
    });
  }

  if (
    isPriceChanged &&
    (priceChangedProducts?.length > 0 || priceChangedOrderItems?.length > 0)
  ) {
    selectedProducts.forEach(item => {
      if (priceChangedProducts?.length > 0) {
        const priceChangedProduct = priceChangedProducts.find(
          priceChangedItem => priceChangedItem.id === item.id
        );

        if (priceChangedProduct) {
          if (
            priceChangedProduct.areAllPriceUnitsDeleted ||
            priceChangedProduct.isOldPriceUnitDeleted ||
            priceChangedProduct.newPriceUnitList?.length
          ) {
            item.priceUnitList = priceChangedProduct.newPriceUnitList.filter(
              priceUnit => priceUnit.isSelected
            );
            item.priceUnitList = item.priceUnitList.map(priceUnit => {
              priceUnit.isSupplierSelected =
                priceUnit.id == priceChangedProduct.newPriceUnit.id;
              return priceUnit;
            });
          } else {
            const indexPriceUnit = item.priceUnitList.findIndex(
              priceUnit => priceUnit.id == priceChangedProduct.newPriceUnit.id
            );
            if (indexPriceUnit == -1) {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                priceUnit.isSupplierSelected = false;
                return priceUnit;
              });
              item.priceUnitList.push({
                ...priceChangedProduct.newPriceUnit,
                isSupplierSelected: true,
              });
            } else {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                if (priceUnit.id == priceChangedProduct.newPriceUnit.id) {
                  return {
                    ...priceUnit,
                    ...priceChangedProduct.newPriceUnit,
                    isSupplierSelected: true,
                  };
                }
                return {
                  ...priceUnit,
                  isSupplierSelected: false,
                };
              });
            }
          }

          item.priceUnit = priceChangedProduct.newPriceUnit;
          item.customerQuantity = priceChangedProduct.newCustomerQuantity;
          if (priceChangedProduct.currentPrice) {
            item.originPrice = priceChangedProduct.currentPrice;
          }
          if (priceChangedProduct.originPrice) {
            item.originPrice = priceChangedProduct.originPrice;
          }
          item.productPricePolicyID = priceChangedProduct.productPricePolicyID;
          item.proposalItemID = priceChangedProduct.proposalItemID;

          state.choiceItemList = state.choiceItemList.map(choiceItem => {
            if (
              choiceItem.id == item.id ||
              choiceItem.proposalItemID == item.proposalItemID ||
              choiceItem.productPricePolicyID == item.productPricePolicyID
            ) {
              choiceItem.priceUnit = item?.priceUnit;
              choiceItem.priceUnitList = item.priceUnitList;
            }
            return choiceItem;
          });
        }
      }

      if (priceChangedOrderItems?.length > 0 && item.orderItemID) {
        const priceChangedOrderItem = priceChangedOrderItems?.find(
          priceChangedItem => priceChangedItem.id === item.orderItemID
        );

        if (priceChangedOrderItem) {
          if (
            priceChangedOrderItem.areAllPriceUnitsDeleted ||
            priceChangedOrderItem.isOldPriceUnitDeleted ||
            priceChangedOrderItem.newPriceUnitList?.length
          ) {
            item.priceUnitList = priceChangedOrderItem.newPriceUnitList.filter(
              priceUnit => priceUnit.isSelected
            );
            item.priceUnitList = item.priceUnitList.map(priceUnit => {
              priceUnit.isSupplierSelected =
                priceUnit.id == priceChangedOrderItem.newPriceUnit.id;
              return priceUnit;
            });
          } else {
            const indexPriceUnit = item.priceUnitList.findIndex(
              priceUnit => priceUnit.id == priceChangedOrderItem.newPriceUnit.id
            );
            if (indexPriceUnit == -1) {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                priceUnit.isSupplierSelected = false;
                return priceUnit;
              });
              item.priceUnitList.push({
                ...priceChangedOrderItem.newPriceUnit,
                isSupplierSelected: true,
              });
            } else {
              item.priceUnitList = item.priceUnitList.map(priceUnit => {
                if (priceUnit.id == priceChangedOrderItem.newPriceUnit.id) {
                  return {
                    ...priceUnit,
                    ...priceChangedOrderItem.newPriceUnit,
                    isSupplierSelected: true,
                  };
                }
                return {
                  ...priceUnit,
                  isSupplierSelected: false,
                };
              });
            }
          }
          item.priceUnit = priceChangedOrderItem.newPriceUnit;
          item.customerQuantity = priceChangedOrderItem.newCustomerQuantity;
          if (priceChangedOrderItem.currentPrice) {
            item.originPrice = priceChangedOrderItem.currentPrice;
          }
          if (priceChangedOrderItem.originPrice) {
            item.originPrice = priceChangedOrderItem.originPrice;
          }
          item.productPricePolicyID =
            priceChangedOrderItem.productPricePolicyID;
          item.proposalItemID = priceChangedOrderItem.proposalItemID;

          state.choiceItemList = state.choiceItemList.map(choiceItem => {
            if (choiceItem.orderItemID == item.orderItemID) {
              choiceItem.priceUnit = item?.priceUnit;
              choiceItem.priceUnitList = item.priceUnitList;
            }
            return choiceItem;
          });
        }
      }
    });
  }

  if (isAddressChanged && changedAddress) {
    let isSelectedAddress = false;
    const foundAddressIndex = state.addressData.findIndex(
      item => item.id === changedAddress.id
    );
    if (foundAddressIndex !== -1) {
      isSelectedAddress = state.addressData[foundAddressIndex].isSelected;
      state.addressData[foundAddressIndex] = {
        ...state.addressData[foundAddressIndex],
        ...changedAddress,
        ward: {
          id: changedAddress.ward?.id,
          code: changedAddress.ward?.code,
          fullName: changedAddress.ward?.fullName,
          district: changedAddress.district,
          province: changedAddress.province,
        },
        district: changedAddress.district,
        province: changedAddress.province,
        isSelected: isSelectedAddress,
      };
    }
    state.address = {
      ...state.address,
      ...changedAddress,
      ward: {
        id: changedAddress.ward.id,
        code: changedAddress.ward?.code,
        fullName: changedAddress.ward?.fullName,
        district: changedAddress.district,
        province: changedAddress.province,
      },
      district: changedAddress.district,
      province: changedAddress.province,
      isSelected: isSelectedAddress,
    };
  }

  if (isOrderEntityShippingPolicyItemChanged) {
    state.shippingPolicyItems =
      changedOrderEntityShippingPolicyItem.entityShippingPolicyItems;
    state.locationShippingFee =
      changedOrderEntityShippingPolicyItem.newOrderEntityShippingPolicyItem;
    state.shippingPolicyItems?.forEach(item => {
      item.isSelected = state.locationShippingFee.id == item.id;
    });
  }

  if (isOrderEntityDistanceShippingFeeChanged) {
    state.distanceShippingFees =
      changedOrderEntityDistanceShippingFee.entityDistanceShippingFees;
    state.distanceShippingFee =
      changedOrderEntityDistanceShippingFee.newOrderEntityDistanceShippingFee;
    state.distanceShippingFees?.forEach(item => {
      item.isSelected = state.distanceShippingFee.id == item.id;
    });
  }

  if (isOrderEntityCustomShippingFeesChanged) {
    state.customShippingFees =
      changedOrderEntityCustomShippingFees.entityCustomShippingFees;
  }

  state.selectedProducts = selectedProducts;
};

const buildItems = async (state: IState, selectedItemList: any[]) => {
  let selectedProducts = [...current(state).selectedProducts];

  try {
    selectedProducts = selectedItemList?.map(item => {
      let findSelectedProduct = current(state)?.selectedProducts?.find(
        (selectedItem: any) => selectedItem.productID == item.productID
      );

      if (findSelectedProduct) {
        findSelectedProduct = {
          ...findSelectedProduct,
          priceUnit: item.priceUnit,
          priceUnitList: (
            item.priceUnitList ?? findSelectedProduct?.priceUnitList
          )
            .filter((price: any) => price.isSelected)
            .map(priceUnit => {
              return {
                ...priceUnit,
                isSupplierSelected: priceUnit.id == item.priceUnit.id,
              };
            }),

          reportQuantity: item.reportQuantity,
          customerQuantity: item.customerQuantity,
          currentPrice: item.currentPrice ?? item.priceUnit.price ?? 0,
          originPrice: item.originPrice ?? item.priceUnit.price ?? 0,
          proposalItemID: item.proposalItemID,
          productPricePolicyID: item.productPricePolicyID,
          productProposalPolicyID: item.productProposalPolicyID,
          promotions: item?.promotions ?? [],
          salePromotionConditions: item?.salePromotionConditions ?? [],
        };

        return findSelectedProduct;
      }

      if (item.productID) {
        let selectedItem = current(state).choiceItemList.find(
          productItem => productItem.productID == item.productID
        );

        if (selectedItem) {
          selectedItem = {
            ...selectedItem,
            priceUnit: item.priceUnit,
            priceUnitList: (item?.priceUnitList ?? selectedItem?.priceUnitList)
              .filter((price: any) => price.isSelected)
              .map(priceUnit => {
                return {
                  ...priceUnit,
                  isSupplierSelected: priceUnit.id == item.priceUnit.id,
                };
              }),

            reportQuantity: item.reportQuantity,
            currentPrice: item.currentPrice ?? item.priceUnit.price ?? 0,
            originPrice: item.originPrice ?? item.priceUnit.price ?? 0,
            customerQuantity: item.customerQuantity,
            proposalItemID: item.proposalItemID,
            productPricePolicyID: item.productPricePolicyID,
            productProposalPolicyID: item.productProposalPolicyID,
            promotions: item?.promotions ?? [],
            salePromotionConditions: item?.salePromotionConditions ?? [],
          };

          return selectedItem;
        }
      }

      return item;
    });

    state.selectedProducts = selectedProducts;
  } catch (error: any) {
    console.log(error);
  }
};

const { actions, reducer: UpdateOrderReducer } = updateOrderSlice;
export default UpdateOrderReducer;
export const {
  updateInformationUpdateOrder,
  updateCustomerNote,
  updateSelectedProducts,
  updateSelectedProductUpdateOrder,
  setChoiceItemList,
  handleResetUpdateOrderData,
  setReloadUpdateOrder,
  revertStateWhenHasBeError,
  updateShouldUpdateOrderData,
  updateSupplierNoteForUpdateOrder,
  resetValidationResultUpdateOrder,
  updateBankAccountInfoUpdateOrderData,
  updateDeliveryNoteUpdateOrderData,
} = actions;

export const updateOrderSelector = (state: RootState): IState =>
  state.updateOrder as IState;
